import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight, ScanLine } from "lucide-react";

import { api } from "@/lib/api";
import { labelFor } from "@/lib/options";
import { StyleMiniCard } from "@/components/cards";
import { Button, SectionTitle, Spinner, WaveBackdrop } from "@/components/kit";
import HorizontalCarousel from "@/components/HorizontalCarousel";
import { AddRoutineButton, LiveBuyBox, LiveImage } from "@/components/shopui";
import { useShop } from "@/shop";
import { useApp } from "@/store";

export function ProfileSummary() {
  const { profile, hasProfile } = useApp();
  const navigate = useNavigate();
  if (!hasProfile) {
    return (
      <div data-testid="home-empty-state" className="relative overflow-hidden rounded-[20px] bg-navy p-6 md:p-8">
        <WaveBackdrop tone="dark" />
        <div className="relative">
          <p className="font-body-bold text-[11px] tracking-[1.4px] text-sky">LET&apos;S GET STARTED</p>
          <h2 className="mt-1 font-display text-2xl leading-7 text-white md:text-3xl">FIND YOUR HAIR PROFILE.</h2>
          <p className="mt-1.5 max-w-lg font-body text-[13px] leading-[19px] text-soft">A quick scan or a few taps unlocks your top 5 styles, your BREEZ products and a daily routine.</p>
          <div className="mt-4 flex max-w-md gap-3">
            <Button testId="home-empty-scan" title="Scan" icon={ScanLine} variant="secondary" onClick={() => navigate("/scan")} className="flex-1" />
            <Button testId="home-empty-manual" title="Manual" variant="glass" onClick={() => navigate("/edit-profile?next=recs")} className="flex-1" />
          </div>
        </div>
      </div>
    );
  }
  const items = [
    ["Face", labelFor("face_shape", profile.face_shape)],
    ["Pattern", profile.hair_pattern ?? labelFor("hair_type", profile.hair_type)],
    ["Length", labelFor("length", profile.length)],
    ["Finish", labelFor("finish", profile.finish)],
  ];
  return (
    <button type="button" data-testid="home-profile-summary" onClick={() => navigate("/mystyle")} className="focus-ring relative block w-full overflow-hidden rounded-[20px] bg-navy p-6 text-left transition-transform active:scale-[0.99]">
      <WaveBackdrop tone="dark" />
      <div className="relative">
        <div className="flex items-center justify-between">
          <span className="font-body-bold text-[11px] tracking-[1.4px] text-sky">YOUR HAIR PROFILE</span>
          <ChevronRight className="h-[18px] w-[18px] text-sky" aria-hidden />
        </div>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {items.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <p className="font-body-bold text-[10px] tracking-[1px] text-soft">{k.toUpperCase()}</p>
              <p className="mt-0.5 truncate font-display-semi text-base text-white">{v}</p>
            </div>
          ))}
        </div>
      </div>
    </button>
  );
}

export function StyleCarousel({ title, styles: list, testPrefix, action, onAction, badges = true }) {
  const navigate = useNavigate();
  const { topSlugs } = useApp();
  return (
    <HorizontalCarousel
      testId={`${testPrefix}-carousel`}
      title={title}
      action={action ? <button type="button" data-testid={`${testPrefix}-see-all`} onClick={onAction} className="focus-ring min-h-[44px] rounded-md px-1 t-label !text-ocean hover:underline">{action}</button> : null}
      rowClassName="-mx-5 gap-3 px-5 pb-1 scroll-px-5 md:mx-0 md:px-0 md:scroll-px-0"
    >
      {list.map((s) => (
        <StyleMiniCard key={s.slug} testId={`${testPrefix}-${s.slug}`} style={s} width={170} forYou={badges && topSlugs.has(s.slug)} onClick={() => navigate(`/style/${s.slug}`)} />
      ))}
    </HorizontalCarousel>
  );
}

/** My BREEZ Products + My Styling Routine, based on the first saved style or the top recommendation. */
export function RoutineSection({ style }) {
  const { profile } = useApp();
  const { live } = useShop();
  const [match, setMatch] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    setError(null);
    api(`/hairstyles/${style.slug}/match`, { method: "POST", body: { profile } })
      .then((r) => alive && setMatch(r.match))
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [style.slug, profile]);

  if (error) return <p className="t-caption">{error}</p>;
  if (!match) return <Spinner />;
  const picks = [match.primary, match.supporting].filter(Boolean);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section data-testid="home-my-products">
        <SectionTitle title="My BREEZ Products" action="Shop" onAction={() => navigate("/shop")} testId="home-products-shop" />
        <div className="grid grid-cols-2 gap-3">
          {picks.map((p, i) => (
            <div key={p.slug} className="flex flex-col gap-1 rounded-[20px] border border-line bg-white p-2 pb-3">
              <button type="button" data-testid={`home-product-${p.slug}`} onClick={() => navigate(`/product/${p.slug}`)} className="focus-ring flex flex-col gap-1 rounded-xl text-left">
                <LiveImage live={live(p.slug)} name={p.name} height={110} />
                <span className="mt-1 font-body-bold text-[10px] tracking-[1px] text-ocean">{i === 0 ? "PRIMARY" : "SUPPORTING"}</span>
                <span className="t-strong line-clamp-2">{live(p.slug)?.title ?? p.name}</span>
              </button>
              <LiveBuyBox slug={p.slug} testPrefix={`home-product-${p.slug}`} compact />
            </div>
          ))}
        </div>
        <div className="mt-3">
          <AddRoutineButton slugs={picks.map((p) => p.slug)} testId="home-add-routine" />
        </div>
      </section>
      <section data-testid="home-my-routine">
        <SectionTitle title="My Styling Routine" action="Full tutorial" onAction={() => navigate(`/style/${style.slug}`)} testId="home-routine-tutorial" />
        <div className="flex flex-col gap-3 rounded-[20px] border border-line bg-white p-4">
          <p className="t-caption">For your {style.name} · about {match.estimated_time} min</p>
          {style.styling_steps.slice(0, 4).map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-navy font-display-bold text-xs text-white">{i + 1}</span>
              <p className="t-body flex-1">{s}</p>
            </div>
          ))}
          {style.styling_steps.length > 4 ? <p className="t-caption">+{style.styling_steps.length - 4} more steps in the full tutorial</p> : null}
        </div>
      </section>
    </div>
  );
}

/** Home "Learn" section: one featured article + see all (hidden when nothing is published). */
export function LearnCard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api("/articles").then(setItems).catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  const a = items[0];
  return (
    <section data-testid="home-learn-section" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="t-h2">Learn</h2>
        <button type="button" data-testid="home-learn-see-all" onClick={() => navigate("/learn")} className="focus-ring min-h-[44px] rounded-md t-strong !text-ocean hover:underline">See all ({items.length})</button>
      </div>
      <button type="button" data-testid="home-learn-card" onClick={() => navigate(`/learn/${a.id}`)} className="focus-ring flex items-center gap-3 rounded-[20px] border border-line bg-white p-3 text-left transition-all hover:border-ocean">
        {a.cover_url ? <img src={a.cover_url} alt="" className="h-[88px] w-[88px] rounded-xl object-cover" /> : (
          <span className="flex h-[88px] w-[88px] items-center justify-center rounded-xl bg-tert"><BookOpen className="h-7 w-7 text-navy" aria-hidden /></span>
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="t-label">Hair school</span>
          <span className="t-title line-clamp-2">{a.title}</span>
          <span className="t-caption line-clamp-2">{a.body}</span>
        </span>
      </button>
    </section>
  );
}
